// manage-response-next-form.component.ts

import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray, FormControl } from "@angular/forms";
import { Subject, firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";

// PrimeNG Modules
import { PrimeNGSharedModule } from "../../../../../shared/prime/prime-ng.module";
import { MessageService } from "primeng/api";
import { ManageAssessmentApiService, ResponseFormApiPayload } from "../../../../../core/services/api/manage-assessment-api.service";
import { HttpErrorResponse } from '@angular/common/http';
import { FirebaseStorageService } from "@core/services/api/firebase-storage.service";
import { ApiService } from "@core/services/api/api.service";
import { environment } from "@environments/environment";

interface UploadedFile {
  name: string;
  url?: string;
  file?: File;
  path?: string;
  id?: number; // existing attachment id when revising
  uploading: boolean; // true while upload in progress
  uploaded: boolean; // true when upload finished successfully
  progress: number; // 0-100
  error?: string; // error message if upload failed
  uploadedAt?: string; // existing attachment timestamp
  bucket?: string;
  localBlobUrl?: string;
}

export interface AttachmentData {
  bucket_name?: string;
  file_path?: string;
  file_name?: string;
  id?: number;
}

@Component({
  selector: "app-manage-response-next-form",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PrimeNGSharedModule],
  templateUrl: "./manage-response-next-form.component.html",
  styleUrl: "./manage-response-next-form.component.scss",
  providers: [MessageService],
})
export class ManageResponseNextFormComponent implements OnInit, OnDestroy {
  @Input() rcaDataFromFirstPage: any; // This will now receive the full combined RCA and Response data
  @Output() goBack = new EventEmitter<void>();
  @Output() formSubmittedEvent = new EventEmitter<any>();
  @Output() saveFormEvent = new EventEmitter<any>();
  @Input() mappingId: any = null; // Contains response_mapping_id and rca_id
  @Input() reviseResponse: boolean = false;
  @Input() isReviseCheck: boolean = false;

  rcaForm!: FormGroup;
  formSubmitted: boolean = false;
  showOtherDocumentationField: boolean = false;

  // Dropdown options (keep these as they are)
  deviceClassificationsOptions: any[] = [
    { id: 1, name: "Class A" },
    { id: 2, name: "Class B" },
    { id: 3, name: "Class C" },
    { id: 4, name: "Class D" },
    { id: 5, name: "Class I" },
    { id: 6, name: "Class I (low risk) - MX Specific" },
    { id: 7, name: "Class II" },
    { id: 8, name: "Class IIa" },
    { id: 9, name: "Class IIb" },
    { id: 10, name: "Class III" },
    { id: 11, name: "Class Im" },
    { id: 12, name: "Class Ir" },
    { id: 13, name: "Class Is" },
    { id: 14, name: 'N/A'},
    { id: 15, name: 'Non-Medical Device'},
  ];

  impactConclusionOptions: any[] = [
    { id: true, name: "Yes" },
    { id: false, name: "No" },
  ];

  yesNoOptions: any[] = [
    { id: true, name: "Yes" },
    { id: false, name: "No" },
  ];

  documentationOptions: any[] = [
    { id: "eudeclaration", name: "EU Declaration of Conformity" },
    { id: "cecertificate", name: "CE Certificate (Full Quality Assurance)" },
    { id: "labels", name: "Primary/Secondary/UDI labels" },
    { id: "ifu", name: "IFU/Operation Manual" },
    { id: "riskmanagement", name: "Risk Management Document" },
    { id: "udi", name: "UDI(GTIN/GLOBAN)" },
    { id: "iso13485", name: "ISO 13485 Certificate" },
    {
      id: "cfg",
      name: "CFG (Certificate of Foreign Government – U.S. document)",
    },
    { id: "coe", name: "COE (Certificate of Exportability – U.S. document)" },
    { id: "fsc", name: "FSC (Free Sales Certificate)" },
    { id: "eutechnicalfile", name: "EU Technical file" },
    { id: "510k", name: "510(k)" },
    { id: "vnv", name: "V&V Documentation (Performance, shelf life, etc.)" },
    { id: "msds", name: "MSDS" },
    { id: "others", name: "Other(s)" },
  ];

  private readonly unsubscribe$ = new Subject<void>();

  /** Full-screen textarea for impact assessment and justification */
  impactAssessJustificationMaxPreview = false;

  /** Full-screen textarea for acceptable duration */
  acceptableDurationMaxPreview = false;

  readonly impactAssessJustificationMaxDialogContentStyle: Record<string, string | number> = {
    overflow: "hidden",
    display: "flex",
    "flex-direction": "column",
    flex: "1 1 auto",
    "min-height": 0,
  };

  readonly impactAssessJustificationMaxTextareaStyle = {
    height: "calc(100vh - 140px)",
    minHeight: "200px",
    resize: "vertical" as const,
  };

  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;
  uploadedFiles: UploadedFile[] = [];
  uploading: boolean = false;
  uploadError: string = "";

  get impactAssessJustificationInvalid(): boolean {
    return this.formSubmitted && !!this.rcaForm?.get("assessmentAndJustification")?.invalid;
  }

  get timeProductNotifyInvalid(): boolean {
    return this.formSubmitted && !!this.rcaForm?.get("timeProductNotify")?.invalid;
  }

  constructor(
  private readonly fb: FormBuilder,
  private readonly messageService: MessageService,
  private readonly manageAssessmentApiService: ManageAssessmentApiService,
  private readonly firebaseStorageService: FirebaseStorageService,
  private readonly cdr: ChangeDetectorRef,
  private readonly apiService: ApiService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.setupConditionalLogic(); // Set up subscription before patching
    this.setupDocumentationRequiredLogic(); // New: Setup logic for 'Other' documentation
    this.setupProductNotifyLogic();
    if (this.rcaDataFromFirstPage) {
      this.patchFormWithFirstPageData(this.rcaDataFromFirstPage);
    }
  }

  initializeForm(): void {
    this.rcaForm = this.fb.group({
      deviceClassification: [null],
      impactConclusion: [null, Validators.required],
      assessmentAndJustification: [""],
      additionalAttachments: [""],
      documentationRequired: [[]],
      otherDocumentationInput: [""],
      timeLocalRegulatory: [""],
      timeHealthAuthority: [""],
      timeProductNotify: [""],
      productAuthority: [null],
      productNotify: [null],
    });
  }

  patchFormWithFirstPageData(data: any): void {
    console.log("Patching second form with data:", data);

    this.patchCoreFields(data);
    this.patchDocumentationSelections(data);
    this.patchResponseAttachments(data);
  }

  private findOptionById(options: any[], id: any) {
    if (id === undefined || id === null) {
      return undefined;
    }
    return options.find((item) => item.id === id);
  }

  private patchCoreFields(data: any): void {
    this.rcaForm.patchValue({
      deviceClassification: this.findOptionById(this.deviceClassificationsOptions, data?.device_class),
      impactConclusion: this.findOptionById(this.impactConclusionOptions, data?.impact_conclusion),
      assessmentAndJustification: data?.impact_assess_justification || "",
      additionalAttachments: data?.response_content || "",
      timeLocalRegulatory: data?.local_authority_eta > 0 ? data.local_authority_eta : "",
      timeHealthAuthority: data?.health_authority_eta > 0 ? data.health_authority_eta : "",
      timeProductNotify: data?.time_product_notify || null,
      productAuthority: this.findOptionById(this.yesNoOptions, data?.prior_approval_acceptance),
      productNotify: this.findOptionById(this.yesNoOptions, data?.post_approval_acceptance),
      otherDocumentationInput: data?.other_documentation || "",
    });
  }

  private patchDocumentationSelections(data: any): void {
    const documentationControl = this.rcaForm.get("documentationRequired");
    if (!documentationControl) {
      return;
    }

    const selectedDocsForPatch = this.buildDocumentationSelections(data);
    documentationControl.patchValue(selectedDocsForPatch);
  }

  private buildDocumentationSelections(data: any): any[] {
    if (!Array.isArray(data?.documentation_required) || !this.documentationOptions.length) {
      return [];
    }

    const selectedDocsForPatch: any[] = [];
    const state = {
      hasOther: false,
      otherValuePresent: Boolean(data?.other_documentation),
    };

    for (const docNameOrId of data.documentation_required) {
      this.tryAddDocumentationOption(selectedDocsForPatch, docNameOrId, state);
    }

    if (state.otherValuePresent && !state.hasOther) {
      this.tryAddOtherDocumentation(selectedDocsForPatch, state);
    }

    return selectedDocsForPatch;
  }

  private getOtherDocumentationOption() {
    return this.documentationOptions.find((opt) => opt.id === "others");
  }

  private tryAddDocumentationOption(selectedDocs: any[], docNameOrId: any, state: { hasOther: boolean; otherValuePresent: boolean }): void {
    const matchedOption = this.documentationOptions.find(
      (opt) => opt.id === docNameOrId || opt.name === docNameOrId
    );

    if (matchedOption) {
      selectedDocs.push(matchedOption);
      if (matchedOption.id === "others") {
        state.hasOther = true;
      }
      return;
    }

    if (state.otherValuePresent && !state.hasOther) {
      this.tryAddOtherDocumentation(selectedDocs, state);
    }
  }

  private tryAddOtherDocumentation(selectedDocs: any[], state: { hasOther: boolean; otherValuePresent: boolean }): void {
    const otherOption = this.getOtherDocumentationOption();
    if (otherOption) {
      selectedDocs.push(otherOption);
      state.hasOther = true;
    }
  }

  hasUploadingFiles(): boolean {
    return this.uploadedFiles?.some((file) => file.uploading) || false;
  }

  private patchResponseAttachments(data: any): void {
    let responseAttachments: any[] = [];

    if (Array.isArray(data?.responseAttachments)) {
      responseAttachments = data.responseAttachments;
    } 

    if ((this.isReviseCheck || this.reviseResponse) && responseAttachments.length > 0) {
      this.uploadedFiles = responseAttachments
        .filter((attachment: any) => attachment?.file_name && attachment?.id)
        .map((attachment: any) => ({
          name: attachment.file_name,
          url: attachment.url || `/api/attachments/download/${attachment.id}`,
          id: attachment.id,
          path: attachment.file_path,
          bucket: attachment.bucket_name,
          uploading: false,
          uploaded: true,
          progress: 100,
          uploadedAt: attachment.uploaded_at,
        }));
    } else {
      this.uploadedFiles = [];
    }
  }

  setupConditionalLogic(): void {
    this.rcaForm
      .get("impactConclusion")
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedImpact) => {
        const impactName = selectedImpact?.name;

        this.resetConditionalFields(); // Reset all conditional fields and their validators

        if (impactName === "Yes") {
          this.enableFieldsForYesImpact();
        } else if (impactName === "No") {
          this.enableFieldsForNoImpact();
        }
        // If impactName is null (initial state or cleared), no additional fields are enabled/required
        this.cdr.detectChanges(); // Ensure UI updates after validator changes
      });
  }

  // New: Logic for 'Other' documentation field
  setupDocumentationRequiredLogic(): void {
    this.rcaForm
      .get("documentationRequired")
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedDocs: any[]) => {
        const otherOptionSelected = selectedDocs?.some((doc) => doc.id === "others");
        this.showOtherDocumentationField = otherOptionSelected;

        const otherDocumentationControl = this.rcaForm.get(
          "otherDocumentationInput"
        );

        if (otherOptionSelected) {
          otherDocumentationControl?.setValidators(Validators.required);
        } else {
          otherDocumentationControl?.clearValidators();
          otherDocumentationControl?.patchValue(""); // Clear value if 'Other' is deselected
        }
        otherDocumentationControl?.updateValueAndValidity();
        this.cdr.detectChanges(); // Important to update UI
      });
  }

  private setupProductNotifyLogic(): void {
    this.rcaForm
      .get("productNotify")
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.updateTimeProductNotifyValidation();
        this.cdr.detectChanges();
      });
  }

  private resetConditionalFields(): void {
    // Fields that are always shown if impactConclusion is selected (Yes or No)
    this.rcaForm.get("assessmentAndJustification")?.clearValidators();
    // Do NOT clear value here, as it might have been patched
    this.rcaForm.get("assessmentAndJustification")?.updateValueAndValidity();

    this.rcaForm.get("additionalAttachments")?.clearValidators();
    // Do NOT clear value here, as it might have been patched
    this.rcaForm.get("additionalAttachments")?.updateValueAndValidity();

    // Fields conditional on 'Yes' impact
    const yesImpactFields = [
      "documentationRequired",
      "timeLocalRegulatory",
      "timeHealthAuthority",
      "productAuthority",
      "productNotify",
      "timeProductNotify",
    ];

    for (const field of yesImpactFields) {
      const control = this.rcaForm.get(field);
      control?.clearValidators();
      control?.updateValueAndValidity();
    }
    // Also reset 'otherDocumentationInput' when impactConclusion changes
    this.rcaForm.get("otherDocumentationInput")?.clearValidators();
    this.rcaForm.get("otherDocumentationInput")?.patchValue("");
    this.rcaForm.get("otherDocumentationInput")?.updateValueAndValidity();
    this.showOtherDocumentationField = false;
  }

  private enableFieldsForYesImpact(): void {
    this.rcaForm
      .get("assessmentAndJustification")
      ?.setValidators(Validators.required);
    this.rcaForm.get("assessmentAndJustification")?.updateValueAndValidity();

    this.rcaForm
      .get("documentationRequired")
      ?.setValidators(Validators.required);
    this.rcaForm.get("documentationRequired")?.updateValueAndValidity();

    const selectedDocs = this.rcaForm.get("documentationRequired")?.value;
    const otherOptionSelected = selectedDocs?.some((doc: any) => doc.id === "others");
    if (otherOptionSelected) {
      this.rcaForm
        .get("otherDocumentationInput")
        ?.setValidators(Validators.required);
    }
    this.rcaForm.get("otherDocumentationInput")?.updateValueAndValidity();

    this.rcaForm
      .get("timeLocalRegulatory")
      ?.setValidators([Validators.required, Validators.pattern(/^\d*$/)]);
    this.rcaForm.get("timeLocalRegulatory")?.updateValueAndValidity();

    this.rcaForm
      .get("timeHealthAuthority")
      ?.setValidators([Validators.required, Validators.pattern(/^\d*$/)]);
    this.rcaForm.get("timeHealthAuthority")?.updateValueAndValidity();

    this.rcaForm.get("productAuthority")?.setValidators(Validators.required);
    this.rcaForm.get("productAuthority")?.updateValueAndValidity();

    this.rcaForm.get("productNotify")?.setValidators(Validators.required);
    this.rcaForm.get("productNotify")?.updateValueAndValidity();

    this.updateTimeProductNotifyValidation();
  }

  private updateTimeProductNotifyValidation(): void {
    const timeProductNotifyControl = this.rcaForm.get("timeProductNotify");
    const shouldRequireTimeProductNotify = this.rcaForm.get("productNotify")?.value?.id === true;

    if (shouldRequireTimeProductNotify) {
      timeProductNotifyControl?.setValidators([Validators.required]);
    } else {
      timeProductNotifyControl?.clearValidators();
      timeProductNotifyControl?.patchValue("");
    }

    timeProductNotifyControl?.updateValueAndValidity({ emitEvent: false });
  }

  private enableFieldsForNoImpact(): void {
    this.rcaForm
      .get("assessmentAndJustification")
      ?.setValidators(Validators.required);
    this.rcaForm.get("assessmentAndJustification")?.updateValueAndValidity();
  }

  async saveForm(): Promise<void> {
    this.formSubmitted = true;
    this.rcaForm.updateValueAndValidity();
    if (this.hasUploadingFiles()) {
      this.messageService.add({
        severity: "warn",
        summary: "Upload In Progress",
        detail: "Please wait for the files to finish uploading before saving.",
        life: 4000,
      });
      return;
    }
    const formValue = this.rcaForm.getRawValue();
    // Ensure queued file gets uploaded before building payload
    await this.ensureQueuedUpload();
    const apiPayload = this._constructApiPayload(formValue);
    if (this.reviseResponse) {
      this.reviseResponseDraft(apiPayload);
    } else {
      this.manageAssessmentApiService
        .saveRcaResponseForm(apiPayload, this.mappingId?.response_mapping_id)
        .subscribe({
          next: (response: any) => {
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: `Response form data saved successfully!`,
              life: 5000,
            });
            this.saveFormEvent.emit(apiPayload);
          },
          error: (error: HttpErrorResponse) => {
            console.error("Failed to save form:", error);
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail:
                error.error?.message ||
                "Failed to save form. Please try again.",
              life: 5000,
            });
          },
        });
    }
  }

  reviseResponseDraft(apiPayload: any) {
    this.manageAssessmentApiService
      .saveRcaReviseResponseForm(
        apiPayload,
        this.mappingId?.response_mapping_id
      )
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Response form data saved successfully!`,
            life: 5000,
          });
          this.saveFormEvent.emit(apiPayload);
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to save form:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message || "Failed to save form. Please try again.",
            life: 5000,
          });
        },
      });
  }

  async submitForm(): Promise<void> {
    this.formSubmitted = true;
    this.rcaForm.updateValueAndValidity();

    if (this.hasUploadingFiles()) {
      this.messageService.add({
        severity: "warn",
        summary: "Upload In Progress",
        detail: "Please wait for the files to finish uploading before submitting.",
        life: 4000,
      });
      return;
    }

    if (this.rcaForm.valid) {
      const formValue = this.rcaForm.getRawValue();
      await this.ensureQueuedUpload();
      const apiPayload = this._constructApiPayload(formValue);
      if (this.reviseResponse) {
        this.reviseResponseSubmit(apiPayload);
      } else {
        this.manageAssessmentApiService
          .submitRcaResponseForm(
            apiPayload,
            this.mappingId?.response_mapping_id
          )
          .subscribe({
            next: (response: any) => {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: `Response form data submitted successfully!`,
                life: 5000,
              });
              this.formSubmittedEvent.emit({
                action: response.message,
              });
            },
            error: (error: HttpErrorResponse) => {
              console.error("Failed to submit form:", error);
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail:
                  error.error?.message ||
                  "Failed to submit form. Please try again.",
                life: 5000,
              });
            },
          });
      }
    } else {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Please fill all required fields before submitting.",
      });
      this.markAllAsDirty(this.rcaForm);
    }
  }

  reviseResponseSubmit(apiPayload: any) {
    this.manageAssessmentApiService
      .submitRcaReviseResponseForm(
        apiPayload,
        this.mappingId?.response_mapping_id
      )
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `Response form data submitted successfully!`,
            life: 5000,
          });
          this.formSubmittedEvent.emit({
            action: "Response form submitted successfully!",
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to submit form:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to submit form. Please try again.",
            life: 5000,
          });
        },
      });
  }

  private markAllAsDirty(formGroup: FormGroup | FormArray): void {
    for (const control of Object.values(formGroup.controls)) {
      if (control instanceof FormControl) {
        control.markAsDirty({ onlySelf: true });
        control.updateValueAndValidity();
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsDirty(control);
      }
    }
  }

  ngOnDestroy(): void {
    this.impactAssessJustificationMaxPreview = false;
    this.acceptableDurationMaxPreview = false;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  openImpactAssessJustificationMaxPreview(): void {
    this.impactAssessJustificationMaxPreview = true;
  }

  closeImpactAssessJustificationMaxPreview(): void {
    this.impactAssessJustificationMaxPreview = false;
  }

  openAcceptableDurationMaxPreview(): void {
    this.acceptableDurationMaxPreview = true;
  }

  closeAcceptableDurationMaxPreview(): void {
    this.acceptableDurationMaxPreview = false;
    this.cdr.detectChanges();
  }

  private _constructApiPayload(formValue: any): ResponseFormApiPayload {
    const attachmentData: AttachmentData[] = [];
    if (this.uploadedFiles?.length) {
      for (const file of this.uploadedFiles) {
        if (file.id) {
          attachmentData.push({ id: file.id });
        } else if (file.bucket && file.path) {
          attachmentData.push({
            bucket_name: file.bucket,
            file_path: file.path,
            file_name: file.name,
          });
        }
      }
    }
    const documentationRequiredIds =
      formValue.documentationRequired?.map((doc: any) => doc.id) || [];

    // Initialize other_documentation to an empty string
    let otherDocumentationValue = "";

    // Check if 'others' is selected in the multiselect
    const isOthersSelected = documentationRequiredIds.includes("others");

    // If 'others' is selected, the value from 'otherDocumentationInput' should be used for other_documentation
    if (isOthersSelected) {
      otherDocumentationValue = formValue.otherDocumentationInput || "";
    }
    // Filter out 'others' from the documentation_required array for the API payload
    // The backend expects only the predefined IDs or the custom 'other' value in 'other_documentation'
    // const finalDocumentationRequired = documentationRequiredIds.filter(
    //   (id: string) => id !== "others"
    // );
    
    const shouldIncludeTimeProductNotify = formValue.productNotify?.id === true;

    // If there's a custom 'otherDocumentationValue' and it's not empty,
    // and 'others' was selected, we don't add it to finalDocumentationRequired.
    // It goes into other_documentation field.
    const payload: ResponseFormApiPayload = {
      device_class: formValue.deviceClassification?.id || 0,
      impact_conclusion: formValue.impactConclusion?.id, // true for Yes, false for No
      impact_assess_justification: formValue.assessmentAndJustification || "",
      prior_approval_acceptance: formValue.productAuthority?.id, // true for Yes, false for No
      post_approval_acceptance: formValue.productNotify?.id, // true for Yes, false for No
      local_authority_eta: Number.parseInt(formValue.timeLocalRegulatory, 10) || null,
      health_authority_eta: Number.parseInt(formValue.timeHealthAuthority, 10) || null,
      response_content: formValue.additionalAttachments || "",
      attachment_data: attachmentData,
      documentation_required: documentationRequiredIds, // Send only the IDs of selected predefined options
      other_documentation: otherDocumentationValue, // Send the custom 'other' text here
      ...(shouldIncludeTimeProductNotify
        ? { time_product_notify: formValue.timeProductNotify || null }
        : {}),
    };
    return payload;
  }

  openFileSelector(): void {
    this.fileInput.nativeElement.click();
  }

  async onFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) {
      return;
    }

    this.uploadError = "";
    const maxSize = 5 * 1024 * 1024; // 5MB per file
    const allowedTypes = new Set([
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ]);

    for (const file of files) {
      if (file.size > maxSize) {
        const message = `${file.name} skipped (size > 5MB).`;
        this.uploadError = message;
        this.messageService.add({ severity: "warn", summary: "File Too Large", detail: message, life: 4000 });
        continue;
      }
      if (!allowedTypes.has(file.type)) {
        const message = `${file.name} unsupported format.`;
        this.uploadError = message;
        this.messageService.add({ severity: "warn", summary: "Invalid Type", detail: message, life: 4000 });
        continue;
      }

      const tempUrl = URL.createObjectURL(file);
      const entry: UploadedFile = {
        name: file.name,
        file,
        url: tempUrl,
        localBlobUrl: tempUrl,
        uploading: false,
        uploaded: false,
        progress: 0,
      };
      this.uploadedFiles.push(entry);
      this.cdr.detectChanges();
      void this.uploadSingleFile(entry);
    }

    input.value = "";
  }

  private async uploadSingleFile(fileEntry: UploadedFile): Promise<void> {
    if (!fileEntry.file) {
      return;
    }

    fileEntry.error = undefined;
    fileEntry.uploading = true;
    fileEntry.progress = 10;
    this.uploading = true;
    this.cdr.detectChanges();

    try {
      const resourceId = this.mappingId?.response_mapping_id || "new-rca-response-id";
      const resourceType = "rca-assessment";
      const downloadUrl = await this.firebaseStorageService.uploadAuthFile(fileEntry.file, resourceType, resourceId);
      const filePath = this.extractPathFromUrl(downloadUrl);
      const bucketName = this.extractBucketFromUrl(downloadUrl);

      fileEntry.url = downloadUrl;
      fileEntry.path = filePath || undefined;
      fileEntry.bucket = bucketName || undefined;
      fileEntry.uploaded = true;
      fileEntry.progress = 100;
      fileEntry.uploading = false;

      if (fileEntry.localBlobUrl) {
        try {
          URL.revokeObjectURL(fileEntry.localBlobUrl);
        } catch {
          // no-op
        }
        fileEntry.localBlobUrl = undefined;
      }

      this.messageService.add({
        severity: "success",
        summary: "Uploaded",
        detail: `${fileEntry.name} uploaded successfully.`,
        life: 3000,
      });
    } catch (error: any) {
      console.error("Upload failed:", error);
      fileEntry.uploading = false;
      fileEntry.uploaded = false;
      fileEntry.progress = 0;
      fileEntry.error = error?.message || "Upload failed";
      this.messageService.add({
        severity: "error",
        summary: "Upload Failed",
        detail: `${fileEntry.name}: ${fileEntry.error}`,
        life: 5000,
      });
    } finally {
      this.uploading = this.uploadedFiles.some((file) => file.uploading);
      this.cdr.detectChanges();
    }
  }

  private async ensureQueuedUpload(): Promise<void> {
    const pendingUploads = this.uploadedFiles.filter(
      (file) => !file.uploaded && !file.id && file.file
    );
    for (const file of pendingUploads) {
      await this.uploadSingleFile(file);
    }
  }

  async downloadFile(url: string | undefined, fileName: string) {
    if (!url) {
      return;
    }
    const isApiDownloadUrl = url.includes("/api/attachments/download/");
    if (isApiDownloadUrl) {
      try {
        const res: any = await firstValueFrom(
          this.apiService.getSignedAttachmentDownloadFromUrl(url)
        );
        if (res?.download_url) {
          const link = document.createElement("a");
          link.href = res.download_url;
          link.download = fileName;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      } catch (err) {
        this.messageService.add({ severity: "error", summary: "Download Failed", detail: "Unable to download file." });
        throw err;
      }
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }

  downloadUploadedOrLocal(file: UploadedFile): void {
    if (file.uploading && file.localBlobUrl) {
      const link = document.createElement("a");
      link.href = file.localBlobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }
    void this.downloadFile(file.url, file.name);
  }

  async removeFile(file: UploadedFile): Promise<void> {
    if (!file || file.uploading) {
      return;
    }

    if (file.id && !file.path) {
      this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file);
      this.cdr.detectChanges();
      this.messageService.add({
        severity: "success",
        summary: "File Removed",
        detail: "Existing attachment removed.",
        life: 3000,
      });
      return;
    }

    if (file.path) {
      try {
        const isAuthBucket = file.bucket
          ? file.bucket === environment.firebaseConfig.storageAuthBucket
          : file.url?.includes(environment.firebaseConfig.storageAuthBucket);
        if (isAuthBucket) {
          await firstValueFrom(this.firebaseStorageService.deleteAuthFile(file.path));
        } else {
          await firstValueFrom(this.firebaseStorageService.deleteFile(file.path));
        }
        this.messageService.add({
          severity: "success",
          summary: "File Removed",
          detail: `${file.name} removed successfully.`,
          life: 3000,
        });
      } catch (error: any) {
        this.messageService.add({
          severity: "error",
          summary: "Removal Failed",
          detail: error?.message || "Failed to remove file.",
          life: 5000,
        });
        return;
      }
    }

    if (file.localBlobUrl) {
      try {
        URL.revokeObjectURL(file.localBlobUrl);
      } catch {
        // ignore
      }
    }

    this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file);
    this.uploadError = "";
    this.uploading = this.uploadedFiles.some((f) => f.uploading);
    this.cdr.detectChanges();
  }

  private extractPathFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split("/");
      const objectPathIndex = pathSegments.indexOf("o");
      if (objectPathIndex > -1 && pathSegments.length > objectPathIndex + 1) {
        const encodedPath = pathSegments.slice(objectPathIndex + 1).join("/");
        return decodeURIComponent(encodedPath);
      }
    } catch (e) {
      console.warn("Could not parse URL to extract path:", url, e);
    }
    return null;
  }

  private extractBucketFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split("/");
      const bucketIndex = pathSegments.indexOf("b");
      if (bucketIndex > -1 && pathSegments.length > bucketIndex + 1) {
        return pathSegments[bucketIndex + 1];
      }
    } catch (e) {
      console.warn("Could not parse URL to extract bucket:", url, e);
    }
    return null;
  }
}